import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, ClipboardCheck, Sparkles, TrendingUp, Trophy, AlertCircle,
  ChevronDown, ChevronUp, CheckCircle2, BookOpen,
} from "lucide-react";
import { useT } from "@/lib/i18n";

function TeamMemberRow({ member, t }) {
  const [open, setOpen] = useState(false);
  const ev = member.evaluations;
  const summary = ev?.last_summary;
  const initials = (member.user.name || "U").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  const scoreColor = ev?.last_passed === true ? "#1B2E7A" : ev?.last_passed === false ? "#DC2626" : "#6B7280";

  return (
    <div
      className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden"
      data-testid={`team-row-${member.user.user_id}`}
    >
      <div className="p-5 flex items-center gap-4 flex-wrap">
        <div className="h-11 w-11 rounded-full bg-[#1B2E7A]/10 text-[#1B2E7A] flex items-center justify-center font-medium">
          {initials || "U"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[#1A1A1A] truncate">{member.user.name}</p>
          <p className="text-xs text-[#6B7280] truncate">{member.user.email}</p>
        </div>
        <div className="grid grid-cols-3 gap-6 text-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">{t("manager.team.col_evals")}</p>
            <p className="font-heading text-lg text-[#1A1A1A]">{ev.completed}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">{t("manager.team.col_avg")}</p>
            <p className="font-heading text-lg" style={{ color: scoreColor }}>
              {ev.avg_score === null ? "—" : `${ev.avg_score}`}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">{t("manager.team.col_courses")}</p>
            <p className="font-heading text-lg text-[#1A1A1A]">{member.courses_passed}</p>
          </div>
        </div>
        {ev.completed > 0 && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-[#1B2E7A] hover:underline inline-flex items-center gap-1"
            data-testid={`team-expand-${member.user.user_id}`}
          >
            {open ? <><ChevronUp className="h-3.5 w-3.5" /> {t("common.see_less")}</> : <><ChevronDown className="h-3.5 w-3.5" /> {t("common.see_more")}</>}
          </button>
        )}
      </div>
      {open && ev.completed > 0 && (
        <div className="border-t border-[#E5E7EB] bg-[#FFFFFF] p-5">
          <div className="flex items-center gap-2 mb-3">
            {ev.last_passed === true ? (
              <Trophy className="h-4 w-4 text-[#1B2E7A]" />
            ) : ev.last_passed === false ? (
              <AlertCircle className="h-4 w-4 text-[#DC2626]" />
            ) : (
              <ClipboardCheck className="h-4 w-4 text-[#6B7280]" />
            )}
            <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">
              {t("manager.team.last_eval")} · {ev.last_score}/100
            </p>
          </div>
          {summary?.summary_es && (
            <p className="text-sm text-[#1A1A1A] leading-relaxed mb-3">{summary.summary_es}</p>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {summary?.strengths_es?.length > 0 && (
              <div className="bg-white rounded-xl p-3 border-l-2 border-[#1B2E7A]">
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#1B2E7A] mb-1.5">
                  <Sparkles className="h-3 w-3 inline mr-1" />{t("eval.strengths")}
                </p>
                <ul className="space-y-1">
                  {summary.strengths_es.slice(0, 3).map((s, i) => (
                    <li key={i} className="text-xs text-[#1A1A1A] inline-flex items-start gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-[#1B2E7A] mt-0.5 shrink-0" /> <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {summary?.to_improve_es?.length > 0 && (
              <div className="bg-white rounded-xl p-3 border-l-2 border-[#DC2626]">
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#DC2626] mb-1.5">
                  {t("eval.to_improve")}
                </p>
                <ul className="space-y-1">
                  {summary.to_improve_es.slice(0, 3).map((s, i) => (
                    <li key={i} className="text-xs text-[#1A1A1A] inline-flex items-start gap-1.5">
                      <AlertCircle className="h-3 w-3 text-[#DC2626] mt-0.5 shrink-0" /> <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManagerTeam() {
  const t = useT();
  const [data, setData] = useState({ team: [], team_size: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/manager/team").then((r) => {
      if (!cancelled) setData(r.data);
    }).catch((e) => {
      if (!cancelled) toast.error(e.response?.data?.detail || t("common.error"));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [t]);

  const totalEvals = data.team.reduce((acc, m) => acc + (m.evaluations?.completed || 0), 0);
  const teamWithScores = data.team.filter((m) => m.evaluations?.avg_score !== null);
  const teamAvg = teamWithScores.length
    ? Math.round(teamWithScores.reduce((acc, m) => acc + (m.evaluations.avg_score || 0), 0) / teamWithScores.length)
    : null;

  return (
    <div data-testid="manager-team-page" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> {t("manager.team.eyebrow")}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
          {t("manager.team.title")}
        </h1>
        <p className="text-[#6B7280] max-w-2xl">{t("manager.team.subtitle")}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="kpi-team-size">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">{t("manager.team.kpi_size")}</p>
          <div className="flex items-end gap-2 mt-1">
            <p className="font-heading text-4xl text-[#1A1A1A]">{data.team_size}</p>
            <Users className="h-5 w-5 text-[#6B7280] mb-1" />
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="kpi-evals">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">{t("manager.team.kpi_evals")}</p>
          <div className="flex items-end gap-2 mt-1">
            <p className="font-heading text-4xl text-[#1A1A1A]">{totalEvals}</p>
            <ClipboardCheck className="h-5 w-5 text-[#6B7280] mb-1" />
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="kpi-avg">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">{t("manager.team.kpi_avg")}</p>
          <div className="flex items-end gap-2 mt-1">
            <p className="font-heading text-4xl text-[#1A1A1A]">{teamAvg === null ? "—" : teamAvg}</p>
            <TrendingUp className="h-5 w-5 text-[#6B7280] mb-1" />
          </div>
        </div>
      </div>

      {/* Team list */}
      <section>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3">{t("manager.team.list_label")}</p>
        {loading ? (
          <div className="text-[#374151]">{t("common.loading")}</div>
        ) : data.team.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-10 text-center">
            <BookOpen className="h-10 w-10 text-[#6B7280] mx-auto mb-2" />
            <p className="text-[#6B7280]">{t("manager.team.empty")}</p>
            <p className="text-xs text-[#6B7280] mt-2">{t("manager.team.empty_hint")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.team.map((m) => (
              <TeamMemberRow key={m.user.user_id} member={m} t={t} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
